import React, { useState, useMemo } from 'react';
import Sidebar from 'components/ui/Sidebar';
import Icon from 'components/AppIcon';
import CaseTable from './components/CaseTable';
import FilterPanel from './components/FilterPanel';
import NewCaseModal from './components/NewCaseModal';

const CaseManagement = () => {
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [isNewCaseModalOpen, setIsNewCaseModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    matterType: '',
    assignedLawyer: '',
    courtJurisdiction: '',
    dateRange: { start: '', end: '' }
  });
  const [selectedCases, setSelectedCases] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Mock case data
  const mockCases = [
    {
      id: 'CASE-2024-001',
      caseNumber: 'HC/CC/001/2024',
      clientName: 'Wanjiku Enterprises Ltd',
      matterType: 'Commercial Litigation',
      status: 'Active',
      assignedAdvocate: 'Sarah Mwangi',
      nextCourtDate: '2024-02-15',
      lastActivity: '2024-01-28',
      priority: 'High',
      courtJurisdiction: 'High Court - Nairobi',
      description: `Commercial dispute involving breach of contract for supply of construction materials. Client seeks damages of KES 2,500,000 plus interest and costs. Matter involves complex contractual interpretation and evidence review.`,
      clientContact: '+254 712 345 678',
      caseValue: 2500000
    },
    {
      id: 'CASE-2024-002',
      caseNumber: 'MAG/CR/045/2024',
      clientName: 'John Kiprotich',
      matterType: 'Criminal Defense',
      status: 'Pending',
      assignedAdvocate: 'Michael Ochieng',
      nextCourtDate: '2024-02-20',
      lastActivity: '2024-01-25',
      priority: 'High',
      courtJurisdiction: 'Magistrate Court - Eldoret',
      description: `Criminal defense case involving charges of fraud. Client maintains innocence and requires comprehensive defense strategy. Case involves financial records analysis and witness examination.`,
      clientContact: '+254 723 456 789',
      caseValue: 0
    },
    {
      id: 'CASE-2024-003',
      caseNumber: 'HC/FAM/012/2024',
      clientName: 'Grace Nyambura',
      matterType: 'Family Law',
      status: 'Active',
      assignedAdvocate: 'Patricia Wanjiru',
      nextCourtDate: '2024-02-18',
      lastActivity: '2024-01-30',
      priority: 'Medium',
      courtJurisdiction: 'High Court - Nakuru',
      description: `Divorce proceedings with child custody and property division matters. Case involves matrimonial property worth KES 8,000,000 and custody arrangements for two minor children.`,
      clientContact: '+254 734 567 890',
      caseValue: 8000000
    },
    {
      id: 'CASE-2024-004',
      caseNumber: 'HC/LAND/078/2024',
      clientName: 'Coastal Properties Ltd',
      matterType: 'Land & Property',
      status: 'Completed',
      assignedAdvocate: 'David Mutua',
      nextCourtDate: null,
      lastActivity: '2024-01-20',
      priority: 'Low',
      courtJurisdiction: 'High Court - Mombasa',
      description: `Land title dispute successfully resolved through negotiated settlement. Client retained ownership of 5-acre beachfront property valued at KES 15,000,000.`,
      clientContact: '+254 745 678 901',
      caseValue: 15000000
    },
    {
      id: 'CASE-2024-005',
      caseNumber: 'HC/CC/089/2024',
      clientName: 'TechStart Kenya Ltd',
      matterType: 'Corporate Law',
      status: 'Active',
      assignedAdvocate: 'Sarah Mwangi',
      nextCourtDate: '2024-02-25',
      lastActivity: '2024-01-29',
      priority: 'Medium',
      courtJurisdiction: 'High Court - Nairobi',
      description: `Corporate restructuring and compliance matter involving merger with international technology firm. Requires regulatory approvals and shareholder agreements.`,
      clientContact: '+254 756 789 012',
      caseValue: 0
    },
    {
      id: 'CASE-2024-006',
      caseNumber: 'ELC/PET/023/2024',
      clientName: 'Citizens Coalition',
      matterType: 'Constitutional Law',
      status: 'Pending',
      assignedAdvocate: 'Michael Ochieng',
      nextCourtDate: '2024-03-05',
      lastActivity: '2024-01-26',
      priority: 'High',
      courtJurisdiction: 'High Court - Nairobi',
      description: `Constitutional petition challenging new taxation regulations. Case involves fundamental rights and constitutional interpretation with potential nationwide impact.`,
      clientContact: '+254 767 890 123',
      caseValue: 0
    }
  ];

  // Filter and search logic
  const filteredCases = useMemo(() => {
    let filtered = mockCases;

    // Apply search filter
    if (searchQuery) {
      filtered = filtered?.filter(case_ =>
        case_?.caseNumber?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        case_?.clientName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        case_?.matterType?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        case_?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Apply filters
    if (filters?.status) {
      filtered = filtered?.filter(case_ => case_?.status === filters?.status);
    }
    if (filters?.matterType) {
      filtered = filtered?.filter(case_ => case_?.matterType === filters?.matterType);
    }
    if (filters?.assignedLawyer) {
      filtered = filtered?.filter(case_ => case_?.assignedAdvocate === filters?.assignedLawyer);
    }
    if (filters?.courtJurisdiction) {
      filtered = filtered?.filter(case_ => case_?.courtJurisdiction === filters?.courtJurisdiction);
    }

    // Apply sorting
    if (sortConfig?.key) {
      filtered?.sort((a, b) => {
        let aValue = a?.[sortConfig?.key];
        let bValue = b?.[sortConfig?.key];

        if (sortConfig?.key === 'nextCourtDate' || sortConfig?.key === 'lastActivity') {
          aValue = aValue ? new Date(aValue) : new Date('1900-01-01');
          bValue = bValue ? new Date(bValue) : new Date('1900-01-01');
        }

        if (aValue < bValue) {
          return sortConfig?.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig?.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [mockCases, searchQuery, filters, sortConfig]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig?.key === key && sortConfig?.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleBulkStatusUpdate = (newStatus) => {
    if (selectedCases?.length === 0) {
      alert('No cases selected');
      return;
    }
    
    if (!newStatus) {
      alert('Please select a status');
      return;
    }
    
    try {
      // Mock bulk update functionality
      console.log(`Updating ${selectedCases?.length} cases to status: ${newStatus}`);
      setSelectedCases([]);
      // In production, this would make an API call
    } catch (error) {
      console.error('Error updating cases:', error);
      alert('Failed to update cases. Please try again.');
    }
  };

  const handleNewCase = (caseData) => {
    if (!caseData) {
      alert('Invalid case data');
      return;
    }
    
    // Validate required fields
    const requiredFields = ['clientName', 'matterType', 'assignedAdvocate'];
    const missingFields = requiredFields?.filter(field => !caseData?.[field]);
    
    if (missingFields?.length > 0) {
      alert(`Please fill in required fields: ${missingFields?.join(', ')}`);
      return;
    }
    
    try {
      // Mock new case creation
      console.log('Creating new case:', caseData);
      setIsNewCaseModalOpen(false);
      // In production, this would make an API call
    } catch (error) {
      console.error('Error creating case:', error);
      alert('Failed to create case. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <div className="md:ml-64 lg:ml-80">
        {/* Header */}
        <div className="bg-surface border-b border-border px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-heading font-bold text-text-primary">Case Management</h1>
              <p className="text-text-secondary mt-1">Manage and track all legal matters</p>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-smooth ${
                  isFilterPanelOpen 
                    ? 'bg-primary text-white border-primary' :'bg-surface text-text-primary border-border hover:bg-secondary-50'
                }`}
              >
                <Icon name="Filter" size={16} />
                <span className="hidden sm:inline">Filters</span>
              </button>
              
              <button
                onClick={() => setIsNewCaseModalOpen(true)}
                className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-smooth"
              >
                <Icon name="Plus" size={16} />
                <span>New Case</span>
              </button>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="px-6 py-4 bg-surface border-b border-border">
          <div className="relative max-w-md">
            <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary" />
            <input
              type="text"
              placeholder="Search cases, clients, or case notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
        </div>

        {/* Filter Panel */}
        {isFilterPanelOpen && (
          <FilterPanel 
            filters={filters}
            setFilters={setFilters}
            onClose={() => setIsFilterPanelOpen(false)}
          />
        )}

        {/* Bulk Actions */}
        {selectedCases?.length > 0 && (
          <div className="px-6 py-3 bg-primary-50 border-b border-border">
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-primary">
                {selectedCases?.length} case{selectedCases?.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleBulkStatusUpdate('Active')}
                  className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-smooth"
                >
                  Mark Active
                </button>
                <button
                  onClick={() => handleBulkStatusUpdate('Pending')}
                  className="px-3 py-1 text-sm bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-smooth"
                >
                  Mark Pending
                </button>
                <button
                  onClick={() => setSelectedCases([])}
                  className="px-3 py-1 text-sm bg-secondary-100 text-text-primary rounded hover:bg-secondary-200 transition-smooth"
                >
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Cases Table */}
        <div className="p-6">
          <CaseTable
            cases={filteredCases}
            selectedCases={selectedCases}
            setSelectedCases={setSelectedCases}
            sortConfig={sortConfig}
            onSort={handleSort}
          />
        </div>
      </div>
      {/* New Case Modal */}
      {isNewCaseModalOpen && (
        <NewCaseModal
          isOpen={isNewCaseModalOpen}
          onClose={() => setIsNewCaseModalOpen(false)}
          onSubmit={handleNewCase}
        />
      )}
    </div>
  );
};

export default CaseManagement;